'use client'

import { TabGroup, tabsListVariants, tabsTriggerVariants } from '@morpho-org/ui-v6/client'
import { cn } from '@morpho-org/ui-v6/utils'
import { BREAKPOINTS_PX } from '@repo/ui/breakpoints'
import { parseAsStringEnum, useQueryState } from 'nuqs'
import { type ReactNode, useEffect, useRef } from 'react'

import { ScrollSection } from '@/components/common/ScrollSection'
import SubTabsStickyBackground from '@/components/common/SubTabsStickyBackground'
import SectionLayout from '@/components/layout/SectionLayout/SectionLayout'
import SimpleActionPanelStickyContainer, {
  SIMPLE_ACTION_PANEL_GRID
} from '@/components/layout/SectionLayout/SimpleActionPanelStickyContainer'
import { useWeb3Context } from '@/contexts/Web3Context'
import { VaultPageTabValues } from '@/helpers/routing'
import { useSectionScrollSpy } from '@/hooks/useSectionScrollSpy'
import { useTrackTabChanged } from '@/lib/analytics/client/hooks/useTrackTabChanged'

export interface VaultDetailPageSection {
  id: string
  label: string
  slot: ReactNode
  requiresConnectedWallet?: boolean
}

export interface VaultDetailPageLayoutProps {
  heroSlot: ReactNode
  simpleActionPanelSlot: ReactNode
  sections: VaultDetailPageSection[]
  subTabOptions: { label: string; value: string }[]
  tabsTestId: string
  segmentId: string
}

const DESKTOP_STICKY_OFFSET = 128
const DESKTOP_MEDIA_QUERY = `(min-width: ${BREAKPOINTS_PX.md}px)`

// Mirrors the `--sticky-offset` custom property set on the sections container:
// a fixed desktop offset, or the stacked mobile chrome heights below `md`.
const getStickyOffset = () => {
  if (typeof window === 'undefined' || window.matchMedia(DESKTOP_MEDIA_QUERY).matches) {
    return DESKTOP_STICKY_OFFSET
  }

  const root = getComputedStyle(document.documentElement)
  const getCssPixels = (property: string) => Number.parseFloat(root.getPropertyValue(property)) || 0

  return (
    getCssPixels('--navbar-height') +
    getCssPixels('--mobile-tabs-height') +
    getCssPixels('--vault-market-sub-tabs-height')
  )
}

export default function VaultDetailPageLayout({
  heroSlot,
  simpleActionPanelSlot,
  sections,
  subTabOptions,
  tabsTestId,
  segmentId
}: VaultDetailPageLayoutProps) {
  const {
    account: { isConnected }
  } = useWeb3Context()
  const [activeTab, setActiveTab] = useQueryState(
    'tab',
    parseAsStringEnum<VaultPageTabValues>(Object.values(VaultPageTabValues))
  )
  const trackTabChanged = useTrackTabChanged('vault')

  const visibleSections = sections.filter(
    ({ requiresConnectedWallet }) => !requiresConnectedWallet || isConnected
  )
  const visibleOptions = subTabOptions.filter(
    ({ value }) => value !== 'yourPosition' || isConnected
  )
  const mobileOptions = visibleOptions.filter(({ value }) => value !== 'yourPosition')

  // One spy serves both viewports: the `yourPosition` section is only laid out
  // on mobile under the My Position tab, and the spy skips zero-size sections.
  const { activeSection, scrollToSection, registerSection } = useSectionScrollSpy({
    sections: visibleSections,
    defaultSection: 'overview',
    stickyOffset: getStickyOffset
  })
  // Starts false so a `#yourPosition` link opened with an already-connected
  // wallet is handled on mount like a fresh connection.
  const previousIsConnected = useRef(false)

  useEffect(() => {
    if (!isConnected && activeTab === VaultPageTabValues.MyPosition) {
      setActiveTab(VaultPageTabValues.Vault)
    }

    const wasConnected = previousIsConnected.current
    previousIsConnected.current = isConnected

    if (!isConnected && wasConnected && activeSection === 'yourPosition') {
      scrollToSection('activity')
    }

    if (isConnected && !wasConnected && window.location.hash.slice(1) === 'yourPosition') {
      setActiveTab(VaultPageTabValues.MyPosition)
      requestAnimationFrame(() => scrollToSection('yourPosition'))
    }
  }, [isConnected, activeTab, setActiveTab, activeSection, scrollToSection])

  const handleSubTabChange = (sectionId: string) => {
    trackTabChanged(sectionId)
    scrollToSection(sectionId)
  }

  const currentTab = activeTab ?? VaultPageTabValues.Vault
  const mobileTabs: { value: VaultPageTabValues; label: string }[] = [
    { value: VaultPageTabValues.Vault, label: 'Vault' },
    ...(isConnected ? [{ value: VaultPageTabValues.MyPosition, label: 'My Position' }] : []),
    {
      value: VaultPageTabValues.ManagePosition,
      label: isConnected ? 'Manage position' : 'Open position'
    }
  ]

  return (
    <div className="group/vault-tab min-w-0 flex-1 md:contents" data-vault-tab={currentTab}>
      <div className={cn('w-full min-w-0', SIMPLE_ACTION_PANEL_GRID)}>
        {/* The tab strip only hides/shows parts of one shared tree via CSS, so it is a
            set of toggle buttons rather than a tablist with dedicated panels. */}
        <div className="bg-background pt-sm mb-sm sticky top-[calc(var(--navbar-height)-1px)] z-2 h-(--mobile-tabs-height) backdrop-blur-[8px] md:hidden">
          <div role="group" className={tabsListVariants()} aria-label="Navigation">
            {mobileTabs.map(({ value, label }) => (
              <button
                key={value}
                type="button"
                className={tabsTriggerVariants()}
                data-state={currentTab === value ? 'active' : 'inactive'}
                aria-pressed={currentTab === value}
                onClick={() => value !== currentTab && setActiveTab(value)}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        <SectionLayout
          hasLargerVerticalPadding
          noBackground
          className="min-w-0 group-data-[vault-tab=managePosition]/vault-tab:max-md:hidden"
        >
          <div
            className={cn(
              'gap-xl max-md:gap-lg flex w-full flex-col',
              '[--sticky-offset:128px] max-md:[--sticky-offset:calc(var(--navbar-height)+var(--mobile-tabs-height)+var(--vault-market-sub-tabs-height))]'
            )}
          >
            <div className="group-data-[vault-tab=myPosition]/vault-tab:max-md:hidden">
              {heroSlot}
            </div>

            <SubTabsStickyBackground
              data-testid={tabsTestId}
              topPosition={0}
              className="w-full group-data-[vault-tab=managePosition]/vault-tab:max-md:hidden group-data-[vault-tab=myPosition]/vault-tab:max-md:hidden"
            >
              <div className="hidden w-full md:block">
                <TabGroup
                  id={segmentId}
                  options={visibleOptions}
                  value={activeSection}
                  onChange={handleSubTabChange}
                  hasActiveBorder
                  className="w-full"
                />
              </div>
              <div className="w-full md:hidden">
                <TabGroup
                  id={`${segmentId}-mobile`}
                  options={mobileOptions}
                  value={activeSection}
                  onChange={handleSubTabChange}
                  className="w-full"
                />
              </div>
            </SubTabsStickyBackground>

            {visibleSections.map(({ id, label, slot }) => (
              <div
                key={id}
                className={cn(
                  id === 'yourPosition'
                    ? 'group-data-[vault-tab=vault]/vault-tab:max-md:hidden group-data-[vault-tab=managePosition]/vault-tab:max-md:hidden'
                    : 'group-data-[vault-tab=myPosition]/vault-tab:max-md:hidden group-data-[vault-tab=managePosition]/vault-tab:max-md:hidden'
                )}
              >
                <ScrollSection
                  id={id}
                  label={label}
                  stickyOffset="var(--sticky-offset)"
                  onRegister={registerSection}
                >
                  {slot}
                </ScrollSection>
              </div>
            ))}
          </div>
        </SectionLayout>

        <div className="max-md:p-sm max-md:mb-[60px] max-md:hidden max-md:w-full group-data-[vault-tab=managePosition]/vault-tab:max-md:block md:contents">
          <SimpleActionPanelStickyContainer
            hideOnMobile={false}
            className="max-md:static max-md:mt-0 max-md:flex max-md:max-h-none max-md:w-full max-md:shrink max-md:overflow-visible max-md:px-0! max-md:pt-0"
          >
            {simpleActionPanelSlot}
          </SimpleActionPanelStickyContainer>
        </div>
      </div>
    </div>
  )
}
